import {
  clinicianWorkbenchDetailTabs,
  clinicianWorkbenchListScopes,
} from "@amplecare/contracts";

import { resolveCanonicalWorkspacePathname } from "../config/runtime-config.js";
import type { DetailTabId, PatientListTabId, WorkbenchPanelId } from "./types.js";

export interface ClinicianWorkbenchRouteState {
  selectedPatientTabId: PatientListTabId;
  selectedWorkbenchPanelId: WorkbenchPanelId;
  requiresAttentionOnly: boolean;
  searchQuery: string;
  selectedPatientId: string | null;
  selectedDetailTabId: DetailTabId;
}

export function areClinicianWorkbenchRouteStatesEqual(
  current: ClinicianWorkbenchRouteState,
  next: ClinicianWorkbenchRouteState,
): boolean {
  return (
    current.selectedPatientTabId === next.selectedPatientTabId &&
    current.selectedWorkbenchPanelId === next.selectedWorkbenchPanelId &&
    current.requiresAttentionOnly === next.requiresAttentionOnly &&
    current.searchQuery === next.searchQuery &&
    current.selectedPatientId === next.selectedPatientId &&
    current.selectedDetailTabId === next.selectedDetailTabId
  );
}

const routeParamKeys = {
  selectedPatientTabId: "list",
  selectedWorkbenchPanelId: "panel",
  requiresAttentionOnly: "attention",
  searchQuery: "search",
  selectedPatientId: "patient",
  selectedDetailTabId: "detail",
} as const;

function getBrowserWindow(): Window | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window;
}

function buildBrowserUrlWithCanonicalPathname(
  browserWindow: Window,
  search: string,
  pathname = browserWindow.location.pathname,
): string {
  return `${resolveCanonicalWorkspacePathname(pathname)}${search}${browserWindow.location.hash}`;
}

function resolveSelectedPatientTabId(value: string | null): PatientListTabId {
  return clinicianWorkbenchListScopes.includes(value as PatientListTabId)
    ? (value as PatientListTabId)
    : "my";
}

function resolveSelectedWorkbenchPanelId(
  value: string | null,
  selectedDetailTabId: DetailTabId,
): WorkbenchPanelId {
  if (value === "tasks" || value === "documents") {
    return value;
  }

  if (selectedDetailTabId === "case-summary") {
    return "tasks";
  }

  if (selectedDetailTabId === "documents") {
    return "documents";
  }

  return "inbox";
}

function resolveSelectedDetailTabId(value: string | null): DetailTabId {
  return clinicianWorkbenchDetailTabs.includes(value as DetailTabId)
    ? (value as DetailTabId)
    : "information";
}

export function readWorkbenchRouteStateFromWindow(): ClinicianWorkbenchRouteState | null {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return null;
  }

  const searchParams = new URLSearchParams(browserWindow.location.search);
  const selectedPatientId = searchParams.get(routeParamKeys.selectedPatientId);
  const selectedDetailTabId = resolveSelectedDetailTabId(
    searchParams.get(routeParamKeys.selectedDetailTabId),
  );

  return {
    selectedPatientTabId: resolveSelectedPatientTabId(
      searchParams.get(routeParamKeys.selectedPatientTabId),
    ),
    selectedWorkbenchPanelId: resolveSelectedWorkbenchPanelId(
      searchParams.get(routeParamKeys.selectedWorkbenchPanelId),
      selectedDetailTabId,
    ),
    requiresAttentionOnly:
      searchParams.get(routeParamKeys.requiresAttentionOnly) === "true",
    searchQuery: searchParams.get(routeParamKeys.searchQuery)?.trim() ?? "",
    selectedPatientId,
    selectedDetailTabId,
  };
}

export function writeWorkbenchRouteStateToWindow(
  routeState: ClinicianWorkbenchRouteState,
  pathname?: string,
): void {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return;
  }

  const searchParams = new URLSearchParams(browserWindow.location.search);

  for (const routeParamKey of Object.values(routeParamKeys)) {
    searchParams.delete(routeParamKey);
  }

  searchParams.set(
    routeParamKeys.selectedPatientTabId,
    routeState.selectedPatientTabId,
  );
  searchParams.set(
    routeParamKeys.selectedWorkbenchPanelId,
    routeState.selectedWorkbenchPanelId,
  );
  searchParams.set(
    routeParamKeys.requiresAttentionOnly,
    String(routeState.requiresAttentionOnly),
  );
  searchParams.set(
    routeParamKeys.selectedDetailTabId,
    routeState.selectedDetailTabId,
  );

  if (routeState.selectedPatientId) {
    searchParams.set(
      routeParamKeys.selectedPatientId,
      routeState.selectedPatientId,
    );
  }

  if (routeState.searchQuery.trim().length > 0) {
    searchParams.set(
      routeParamKeys.searchQuery,
      routeState.searchQuery.trim(),
    );
  }

  const nextSearch = `?${searchParams.toString()}`;
  const nextUrl = buildBrowserUrlWithCanonicalPathname(
    browserWindow,
    nextSearch,
    pathname,
  );

  if (
    `${browserWindow.location.pathname}${browserWindow.location.search}${browserWindow.location.hash}` ===
    nextUrl
  ) {
    return;
  }

  browserWindow.history.replaceState(null, "", nextUrl);
}

export function replaceWorkbenchPathnameInWindow(pathname: string): void {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return;
  }

  const nextUrl = buildBrowserUrlWithCanonicalPathname(
    browserWindow,
    browserWindow.location.search,
    pathname,
  );

  if (
    `${browserWindow.location.pathname}${browserWindow.location.search}${browserWindow.location.hash}` ===
    nextUrl
  ) {
    return;
  }

  browserWindow.history.replaceState(null, "", nextUrl);
}

export function clearWorkbenchRouteStateFromWindow(): void {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return;
  }

  const searchParams = new URLSearchParams(browserWindow.location.search);

  for (const routeParamKey of Object.values(routeParamKeys)) {
    searchParams.delete(routeParamKey);
  }

  const nextSearch = searchParams.toString();

  browserWindow.history.replaceState(
    null,
    "",
    buildBrowserUrlWithCanonicalPathname(
      browserWindow,
      nextSearch ? `?${nextSearch}` : "",
    ),
  );
}

export function subscribeToWorkbenchRouteState(
  onRouteStateChange: (routeState: ClinicianWorkbenchRouteState) => void,
): () => void {
  const browserWindow = getBrowserWindow();

  if (!browserWindow) {
    return () => {};
  }

  const handlePopState = () => {
    const routeState = readWorkbenchRouteStateFromWindow();

    if (routeState) {
      onRouteStateChange(routeState);
    }
  };

  browserWindow.addEventListener("popstate", handlePopState);

  return () => {
    browserWindow.removeEventListener("popstate", handlePopState);
  };
}
